import React, { useContext, useEffect, useReducer, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Link } from "react-router-dom";
import { Store } from "../../Store";
import { getError } from "../../utils";
import { api } from "../../Api";

/* ──────────────────────────────────────────────────────────────────────────
   Convert History (Admin) — site-wide list of point/balance conversions with
   date filter, type/status filters, username search, pagination, and totals.
   ────────────────────────────────────────────────────────────────────── */

const reducer = (state, action) => {
  switch (action.type) {
    case "FETCH_REQUEST":
      return { ...state, loading: true, error: "" };
    case "FETCH_SUCCESS":
      return { ...state, data: action.payload, loading: false };
    case "FETCH_FAIL":
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

const fmtNum = (n) =>
  n === undefined || n === null
    ? "0"
    : Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
const fmtMoney = (n) => "৳ " + fmtNum(n);
const toLocalISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayISO = () => toLocalISODate(new Date());
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toLocalISODate(d);
};
const startOfMonthISO = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset, 1);
  return toLocalISODate(d);
};
const endOfMonthISO = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset + 1, 0);
  return toLocalISODate(d);
};
const initials = (s) => (s || "?").toString().trim().slice(0, 2).toUpperCase();
const fmtDateTime = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  return (
    date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) +
    " · " +
    date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
  );
};

const PRESETS = [
  { key: "today", label: "Today", icon: "fa-sun-o" },
  { key: "yesterday", label: "Yesterday", icon: "fa-moon-o" },
  { key: "7d", label: "Last 7 Days", icon: "fa-calendar-o" },
  { key: "30d", label: "Last 30 Days", icon: "fa-calendar" },
  { key: "thisMonth", label: "This Month", icon: "fa-calendar-check-o" },
  { key: "lastMonth", label: "Last Month", icon: "fa-calendar-minus-o" },
];

const FROM_OPTS = [
  { v: "", label: "Any" },
  { v: "point", label: "Point" },
  { v: "availablebalance", label: "Available" },
  { v: "shoppingbalance", label: "Shopping" },
  { v: "shoppingpoint", label: "Shopping Point" },
  { v: "psipoint", label: "PSI" },
  { v: "matching", label: "Matching" },
];
const TO_OPTS = [
  { v: "", label: "Any" },
  { v: "point", label: "Point" },
  { v: "availablebalance", label: "Available" },
  { v: "shoppingbalance", label: "Shopping" },
];
const STATUS_OPTS = [
  { v: "", label: "Any" },
  { v: "success", label: "Success" },
  { v: "pending", label: "Pending" },
  { v: "failed", label: "Failed" },
  { v: "cancelled", label: "Cancelled" },
];

const TYPE_COLORS = {
  point: "#f59e0b",
  availablebalance: "#10b981",
  shoppingbalance: "#8b5cf6",
  shoppingpoint: "#d97706",
  psipoint: "#7e22ce",
  matching: "#6366f1",
};
const STATUS_COLORS = {
  success: "#16a34a",
  pending: "#f59e0b",
  failed: "#dc2626",
  cancelled: "#6b7280",
};

export default function ConvertHistoryScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [{ loading, error, data }, dispatch] = useReducer(reducer, {
    loading: true,
    error: "",
    data: null,
  });

  // Filter state
  const [startDate, setStartDate] = useState(daysAgoISO(6));
  const [endDate, setEndDate] = useState(todayISO());
  const [activePreset, setActivePreset] = useState("7d");
  const [usernameInput, setUsernameInput] = useState("");
  const [username, setUsername] = useState(""); // committed (used in fetch)
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      dispatch({ type: "FETCH_REQUEST" });
      try {
        const params = new URLSearchParams({
          startDate,
          endDate,
          page: String(page),
          pageSize: String(pageSize),
        });
        if (username) params.set("username", username);
        if (from) params.set("from", from);
        if (to) params.set("to", to);
        if (status) params.set("status", status);
        const { data: payload } = await axios.get(
          `${api}/api/users/admin/converthistory?${params.toString()}`,
          { headers: { Authorization: `Bearer ${userInfo.token}` } }
        );
        if (!cancelled) dispatch({ type: "FETCH_SUCCESS", payload });
      } catch (err) {
        if (!cancelled) dispatch({ type: "FETCH_FAIL", payload: getError(err) });
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [userInfo.token, startDate, endDate, username, from, to, status, page, pageSize]);

  const applyPreset = (key) => {
    setActivePreset(key);
    setPage(1);
    const today = todayISO();
    switch (key) {
      case "today":
        setStartDate(today);
        setEndDate(today);
        break;
      case "yesterday": {
        const y = daysAgoISO(1);
        setStartDate(y);
        setEndDate(y);
        break;
      }
      case "7d":
        setStartDate(daysAgoISO(6));
        setEndDate(today);
        break;
      case "30d":
        setStartDate(daysAgoISO(29));
        setEndDate(today);
        break;
      case "thisMonth":
        setStartDate(startOfMonthISO(0));
        setEndDate(today);
        break;
      case "lastMonth":
        setStartDate(startOfMonthISO(-1));
        setEndDate(endOfMonthISO(-1));
        break;
      default:
        break;
    }
  };

  const submitUsername = (e) => {
    e.preventDefault();
    setPage(1);
    setUsername(usernameInput.trim());
  };
  const clearUsername = () => {
    setUsernameInput("");
    setUsername("");
    setPage(1);
  };
  const clearAllFilters = () => {
    setUsernameInput("");
    setUsername("");
    setFrom("");
    setTo("");
    setStatus("");
    setPage(1);
    applyPreset("7d");
  };

  const totalPages = data?.pages || 0;

  return (
    <div style={S.shell}>
      <Helmet>
        <title>Convert History · Admin</title>
      </Helmet>

      {/* HEADER */}
      <div style={S.hero}>
        <div>
          <h1 style={S.heroTitle}>
            <i className="fa fa-exchange" style={{ marginRight: 12 }} />
            Convert History
          </h1>
          <p style={S.heroSub}>
            Site-wide conversions between point, available balance, shopping balance and matching wallets.
          </p>
        </div>
        {data && (
          <div style={S.heroBadge}>
            <div style={S.heroBadgeLabel}>Window</div>
            <div style={S.heroBadgeValue}>
              {data.range.startLabel} → {data.range.endLabel}
            </div>
          </div>
        )}
      </div>

      {/* DATE FILTER */}
      <div style={S.card}>
        <div style={S.cardHeadRow}>
          <div style={S.sectionTitle}>
            <i className="fa fa-filter" style={{ marginRight: 8, color: "#6366f1" }} />
            Date Range
          </div>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            {data && (
              <>
                Showing <strong>{fmtNum(data.totals.count)}</strong> conversions ·{" "}
                <strong>{fmtNum(data.totals.uniqueUsers)}</strong> users
                {loading && (
                  <span style={{ marginLeft: 10, color: "#6366f1" }}>
                    <i className="fa fa-spinner fa-spin" /> loading…
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        <div style={S.presetRow}>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => applyPreset(p.key)}
              style={{ ...S.presetBtn, ...(activePreset === p.key ? S.presetBtnActive : {}) }}
            >
              <i className={`fa ${p.icon}`} /> {p.label}
            </button>
          ))}
        </div>

        <div style={S.customRow}>
          <div style={S.dateGroup}>
            <label style={S.dateLabel}>From</label>
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => {
                setActivePreset("custom");
                setPage(1);
                setStartDate(e.target.value);
              }}
              style={S.dateInput}
            />
          </div>
          <i className="fa fa-arrow-right" style={{ color: "#94a3b8", alignSelf: "flex-end", paddingBottom: 10 }} />
          <div style={S.dateGroup}>
            <label style={S.dateLabel}>To</label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={todayISO()}
              onChange={(e) => {
                setActivePreset("custom");
                setPage(1);
                setEndDate(e.target.value);
              }}
              style={S.dateInput}
            />
          </div>
          {activePreset === "custom" && (
            <span style={{ ...S.presetBtn, ...S.presetBtnActive, alignSelf: "flex-end", cursor: "default" }}>
              <i className="fa fa-pencil" /> Custom
            </span>
          )}
        </div>
      </div>

      {/* SECONDARY FILTERS */}
      <div style={S.card}>
        <div style={S.sectionTitle}>
          <i className="fa fa-sliders" style={{ marginRight: 8, color: "#6366f1" }} />
          Filters
        </div>
        <div style={S.filterRow}>
          <form onSubmit={submitUsername} style={S.searchForm}>
            <div style={S.searchInputWrap}>
              <i className="fa fa-search" style={{ color: "#94a3b8", marginLeft: 12 }} />
              <input
                type="text"
                placeholder="Search by username (starts with)…"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
                style={S.searchInput}
              />
              {(usernameInput || username) && (
                <button type="button" onClick={clearUsername} style={S.clearBtn} title="Clear">
                  <i className="fa fa-times" />
                </button>
              )}
            </div>
            <button type="submit" style={S.searchBtn}>
              <i className="fa fa-search" /> Search
            </button>
          </form>

          <div style={S.dateGroup}>
            <label style={S.dateLabel}>From wallet</label>
            <select value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }} style={S.dateInput}>
              {FROM_OPTS.map((o) => (
                <option key={o.v} value={o.v}>{o.label}</option>
              ))}
            </select>
          </div>
          <div style={S.dateGroup}>
            <label style={S.dateLabel}>To wallet</label>
            <select value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }} style={S.dateInput}>
              {TO_OPTS.map((o) => (
                <option key={o.v} value={o.v}>{o.label}</option>
              ))}
            </select>
          </div>
          <div style={S.dateGroup}>
            <label style={S.dateLabel}>Status</label>
            <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} style={S.dateInput}>
              {STATUS_OPTS.map((o) => (
                <option key={o.v} value={o.v}>{o.label}</option>
              ))}
            </select>
          </div>
          <div style={S.dateGroup}>
            <label style={S.dateLabel}>Per page</label>
            <select value={pageSize} onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }} style={S.dateInput}>
              {[20, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          {(username || from || to || status) && (
            <button onClick={clearAllFilters} style={{ ...S.searchBtn, background: "#fff", color: "#475569", border: "1px solid #cbd5e1", alignSelf: "flex-end" }}>
              <i className="fa fa-times-circle" /> Clear all
            </button>
          )}
        </div>
        {(username || from || to || status) && (
          <div style={S.activeFilterStrip}>
            <span style={{ fontWeight: 700, fontSize: 11, color: "#92400e", textTransform: "uppercase" }}>
              <i className="fa fa-info-circle" /> Active:
            </span>
            {username && <Chip label={`username: ${username}`} onClear={clearUsername} />}
            {from && <Chip label={`from: ${from}`} onClear={() => setFrom("")} />}
            {to && <Chip label={`to: ${to}`} onClear={() => setTo("")} />}
            {status && <Chip label={`status: ${status}`} onClear={() => setStatus("")} />}
          </div>
        )}
      </div>

      {/* KPI CARDS */}
      {data && (
        <div style={S.kpiRow}>
          <KpiCard
            icon="fa-list-ol"
            label="Conversions"
            value={fmtNum(data.totals.count)}
            sub={`${fmtNum(data.totals.uniqueUsers)} unique users`}
            color="#6366f1"
          />
          <KpiCard
            icon="fa-arrow-circle-right"
            label="Total Amount"
            value={fmtNum(data.totals.totalAmount)}
            sub={`gross from-side total`}
            color="#0ea5e9"
          />
          <KpiCard
            icon="fa-check-circle"
            label="Net Delivered"
            value={fmtNum(data.totals.totalNet)}
            sub={`after fees`}
            color="#10b981"
          />
          <KpiCard
            icon="fa-percent"
            label="Total Fees"
            value={fmtNum(data.totals.totalFee)}
            sub={`platform earnings`}
            color="#f59e0b"
          />
        </div>
      )}

      {/* BREAKDOWN: by from→to + by status */}
      {data && (data.breakdown.byFromTo.length > 0 || data.breakdown.byStatus.length > 0) && (
        <div style={S.breakdownGrid}>
          <div style={S.card}>
            <div style={S.sectionTitle}>
              <i className="fa fa-random" style={{ marginRight: 8, color: "#6366f1" }} />
              Conversion Routes
            </div>
            {data.breakdown.byFromTo.length === 0 ? (
              <Empty message="No conversions in this window" />
            ) : (
              <div style={S.routeList}>
                {data.breakdown.byFromTo.map((r, i) => (
                  <div key={i} style={S.routeRow}>
                    <TypeBadge type={r._id.from} />
                    <i className="fa fa-arrow-right" style={{ color: "#94a3b8", margin: "0 4px" }} />
                    <TypeBadge type={r._id.to} />
                    <div style={{ flex: 1 }} />
                    <div style={S.routeStats}>
                      <span style={{ fontWeight: 700, color: "#0f172a" }}>{fmtNum(r.count)}</span>
                      <span style={{ color: "#64748b", fontSize: 12 }}>conversions</span>
                      <span style={{ fontWeight: 700, color: "#10b981", marginLeft: 12 }}>
                        {fmtNum(r.amount)}
                      </span>
                      <span style={{ color: "#64748b", fontSize: 12 }}>amount</span>
                      {r.fee > 0 && (
                        <>
                          <span style={{ fontWeight: 700, color: "#f59e0b", marginLeft: 12 }}>
                            {fmtNum(r.fee)}
                          </span>
                          <span style={{ color: "#64748b", fontSize: 12 }}>fee</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={S.card}>
            <div style={S.sectionTitle}>
              <i className="fa fa-tasks" style={{ marginRight: 8, color: "#6366f1" }} />
              By Status
            </div>
            {data.breakdown.byStatus.length === 0 ? (
              <Empty message="No data" />
            ) : (
              <div style={S.routeList}>
                {data.breakdown.byStatus.map((s, i) => {
                  const total = data.breakdown.byStatus.reduce((a, b) => a + b.count, 0);
                  const pct = total > 0 ? ((s.count / total) * 100).toFixed(1) : 0;
                  return (
                    <div key={i} style={S.routeRow}>
                      <StatusPill status={s._id} />
                      <div style={{ flex: 1, marginLeft: 12 }}>
                        <div style={S.statusBarTrack}>
                          <div
                            style={{
                              ...S.statusBarFill,
                              width: `${pct}%`,
                              background: STATUS_COLORS[s._id] || "#64748b",
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ minWidth: 130, textAlign: "right" }}>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>{fmtNum(s.count)}</span>
                        <span style={{ color: "#64748b", fontSize: 12, marginLeft: 6 }}>
                          ({pct}%) · {fmtMoney(s.amount)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TABLE */}
      <div style={S.card}>
        <div style={S.sectionTitle}>
          <i className="fa fa-list" style={{ marginRight: 8, color: "#6366f1" }} />
          Conversions
          {data && (
            <span style={S.sectionCount}>
              page {data.page} / {totalPages || 1}
            </span>
          )}
        </div>

        {loading && !data ? (
          <div style={S.loadingBox}>
            <div style={S.spinner} />
            <p style={{ marginTop: 12, color: "#64748b" }}>Loading conversions…</p>
          </div>
        ) : error ? (
          <div style={S.errorBox}>
            <i className="fa fa-exclamation-triangle" style={{ fontSize: 28, color: "#dc2626" }} />
            <p style={{ marginTop: 12, color: "#7f1d1d" }}>{error}</p>
          </div>
        ) : !data || data.products.length === 0 ? (
          <Empty
            icon="fa-inbox"
            message={`No conversions match these filters between ${data?.range.startLabel} and ${data?.range.endLabel}.`}
          />
        ) : (
          <>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>When</th>
                    <th style={S.th}>User</th>
                    <th style={S.th}>From</th>
                    <th style={S.th}>To</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Amount</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Fee</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Net</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Bal. Before</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Bal. After</th>
                    <th style={S.th}>Status</th>
                    <th style={S.th}>Approved By</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.products.map((c) => (
                    <tr key={c._id} style={S.tr}>
                      <td style={S.td}>{fmtDateTime(c.createdAt)}</td>
                      <td style={S.td}>
                        {c.user ? (
                          <div style={S.userCell}>
                            <div style={S.avatar}>{initials(c.user.name)}</div>
                            <div>
                              <div style={S.userName}>{c.user.name}</div>
                              <div style={S.userSub}>
                                @{c.user.username}
                                {c.user.isBlocked && <Mini text="Blocked" />}
                                {c.user.status === false && <Mini text="Disabled" />}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>(deleted user)</span>
                        )}
                      </td>
                      <td style={S.td}><TypeBadge type={c.from} /></td>
                      <td style={S.td}><TypeBadge type={c.to} /></td>
                      <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: "#0f172a" }}>
                        {fmtNum(c.amount)}
                      </td>
                      <td style={{ ...S.td, textAlign: "right", color: c.feeApplied > 0 ? "#f59e0b" : "#94a3b8" }}>
                        {fmtNum(c.feeApplied)}
                      </td>
                      <td style={{ ...S.td, textAlign: "right", fontWeight: 600, color: "#10b981" }}>
                        {fmtNum(c.netAmount)}
                      </td>
                      <td style={{ ...S.td, textAlign: "right", color: "#64748b" }}>
                        {fmtNum(c.balanceBefore)}
                      </td>
                      <td style={{ ...S.td, textAlign: "right", color: "#64748b" }}>
                        {fmtNum(c.balanceAfter)}
                      </td>
                      <td style={S.td}><StatusPill status={c.status} /></td>
                      <td style={{ ...S.td, fontSize: 12, color: "#64748b" }}>
                        {c.approvedBy ? "admin" : "—"}
                      </td>
                      <td style={S.td}>
                        {c.user && (
                          <Link to={`/admin/userreport/${c.userId}`} style={S.linkBtn}>
                            <i className="fa fa-bar-chart" /> Report
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination page={data.page} pages={totalPages} onChange={setPage} />
            )}
          </>
        )}
      </div>

      <ResponsiveCSS />
    </div>
  );
}

/* ────────────── small components ────────────── */

function KpiCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ ...S.kpiCard, borderTop: `3px solid ${color}` }}>
      <div style={{ ...S.kpiIcon, background: color }}>
        <i className={`fa ${icon}`} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.kpiLabel}>{label}</div>
        <div style={S.kpiValue}>{value}</div>
        <div style={S.kpiSub}>{sub}</div>
      </div>
    </div>
  );
}

function TypeBadge({ type }) {
  if (!type) return <span style={{ color: "#94a3b8" }}>—</span>;
  const color = TYPE_COLORS[type] || "#64748b";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".4px",
        color: "#fff",
        background: color,
        whiteSpace: "nowrap",
      }}
    >
      {type === "availablebalance" ? "Available" : type === "shoppingbalance" ? "Shopping" : type === "shoppingpoint" ? "Shopping Point" : type === "psipoint" ? "PSI" : type}
    </span>
  );
}

function StatusPill({ status }) {
  if (!status) return <span style={{ color: "#94a3b8" }}>—</span>;
  const color = STATUS_COLORS[status] || "#6366f1";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".4px",
        color: "#fff",
        background: color,
      }}
    >
      {status}
    </span>
  );
}

function Mini({ text }) {
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 700,
        padding: "1px 6px",
        borderRadius: 99,
        background: "#dc2626",
        color: "#fff",
        marginLeft: 6,
        textTransform: "uppercase",
        letterSpacing: ".5px",
      }}
    >
      {text}
    </span>
  );
}

function Chip({ label, onClear }) {
  return (
    <span style={S.chip}>
      {label}
      <button onClick={onClear} style={S.chipBtn} title="Remove">
        <i className="fa fa-times" />
      </button>
    </span>
  );
}

function Empty({ icon = "fa-inbox", message }) {
  return (
    <div style={S.emptyBox}>
      <i className={`fa ${icon}`} style={{ fontSize: 38, color: "#cbd5e1" }} />
      <p style={{ marginTop: 12, color: "#94a3b8" }}>{message}</p>
    </div>
  );
}

function Pagination({ page, pages, onChange }) {
  const max = 7;
  let start = Math.max(1, page - Math.floor(max / 2));
  let end = Math.min(pages, start + max - 1);
  if (end - start < max - 1) start = Math.max(1, end - max + 1);
  const nums = [];
  for (let i = start; i <= end; i++) nums.push(i);
  return (
    <div style={S.pagWrap}>
      <button onClick={() => onChange(1)} disabled={page <= 1} style={S.pagBtn(page <= 1)}>
        <i className="fa fa-angle-double-left" />
      </button>
      <button onClick={() => onChange(page - 1)} disabled={page <= 1} style={S.pagBtn(page <= 1)}>
        <i className="fa fa-angle-left" /> Prev
      </button>
      {start > 1 && <span style={S.pagDots}>…</span>}
      {nums.map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          style={{ ...S.pagBtn(false), ...(n === page ? S.pagBtnActive : {}) }}
        >
          {n}
        </button>
      ))}
      {end < pages && <span style={S.pagDots}>…</span>}
      <button onClick={() => onChange(page + 1)} disabled={page >= pages} style={S.pagBtn(page >= pages)}>
        Next <i className="fa fa-angle-right" />
      </button>
      <button onClick={() => onChange(pages)} disabled={page >= pages} style={S.pagBtn(page >= pages)}>
        <i className="fa fa-angle-double-right" />
      </button>
    </div>
  );
}

/* ────────────── styles ────────────── */
const S = {
  shell: {
    padding: 24,
    maxWidth: 1500,
    margin: "0 auto",
    background: "#f8fafc",
    minHeight: "100vh",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 20,
    padding: 24,
    background: "linear-gradient(135deg,#0ea5e9,#6366f1)",
    borderRadius: 16,
    color: "#fff",
    boxShadow: "0 10px 24px rgba(99,102,241,.3)",
  },
  heroTitle: { margin: 0, fontSize: 26, fontWeight: 800 },
  heroSub: { margin: "6px 0 0", fontSize: 13, opacity: 0.9 },
  heroBadge: {
    background: "rgba(255,255,255,.95)",
    color: "#0f172a",
    padding: "12px 20px",
    borderRadius: 12,
    boxShadow: "0 6px 18px rgba(0,0,0,.15)",
  },
  heroBadgeLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: ".5px",
    marginBottom: 4,
  },
  heroBadgeValue: { fontSize: 16, fontWeight: 800, color: "#0f172a" },

  card: {
    background: "#fff",
    borderRadius: 14,
    padding: 20,
    border: "1px solid #e2e8f0",
    marginBottom: 16,
    boxShadow: "0 1px 3px rgba(15,23,42,.04)",
  },
  cardHeadRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 12,
  },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: "#0f172a", display: "inline-flex", alignItems: "center" },
  sectionCount: {
    marginLeft: 8,
    background: "#eef2ff",
    color: "#4f46e5",
    padding: "2px 10px",
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 700,
  },

  presetRow: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 },
  presetBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 14px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    color: "#475569",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 99,
    cursor: "pointer",
  },
  presetBtnActive: {
    background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    color: "#fff",
    borderColor: "transparent",
    boxShadow: "0 3px 8px rgba(99,102,241,.25)",
  },
  customRow: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
    flexWrap: "wrap",
    paddingTop: 12,
    borderTop: "1px dashed #e2e8f0",
  },
  dateGroup: { display: "flex", flexDirection: "column", gap: 4 },
  dateLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: ".5px",
  },
  dateInput: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    color: "#0f172a",
    outline: "none",
    fontFamily: "inherit",
    background: "#fff",
  },

  filterRow: { display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginTop: 8 },
  searchForm: { display: "flex", gap: 6, flex: 1, minWidth: 280 },
  searchInputWrap: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#fff",
  },
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    padding: "8px 12px",
    fontSize: 13,
    fontFamily: "inherit",
  },
  clearBtn: {
    background: "transparent",
    border: "none",
    color: "#94a3b8",
    cursor: "pointer",
    padding: "8px 12px",
  },
  searchBtn: {
    padding: "8px 16px",
    background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  activeFilterStrip: {
    marginTop: 12,
    padding: "8px 12px",
    background: "#fffbeb",
    borderRadius: 8,
    border: "1px solid #fde68a",
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 4px 3px 10px",
    background: "#fff",
    border: "1px solid #fcd34d",
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 600,
    color: "#92400e",
  },
  chipBtn: {
    background: "transparent",
    border: "none",
    color: "#92400e",
    cursor: "pointer",
    fontSize: 11,
    padding: "0 6px",
  },

  // KPI
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
    marginBottom: 16,
  },
  kpiCard: {
    display: "flex",
    gap: 12,
    background: "#fff",
    padding: 18,
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(15,23,42,.04)",
  },
  kpiIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    flexShrink: 0,
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: ".5px",
  },
  kpiValue: { fontSize: 22, fontWeight: 800, color: "#0f172a", lineHeight: 1.2, marginTop: 4 },
  kpiSub: { fontSize: 11, color: "#64748b", marginTop: 4 },

  // Breakdown
  breakdownGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
    gap: 16,
    marginBottom: 16,
  },
  routeList: { display: "grid", gap: 8, marginTop: 12 },
  routeRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 12px",
    background: "#f8fafc",
    borderRadius: 10,
    flexWrap: "wrap",
  },
  routeStats: { display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" },
  statusBarTrack: {
    width: "100%",
    height: 8,
    background: "#e2e8f0",
    borderRadius: 99,
    overflow: "hidden",
  },
  statusBarFill: {
    height: "100%",
    transition: "width .3s",
  },

  // Table
  tableWrap: { overflowX: "auto", WebkitOverflowScrolling: "touch", marginTop: 4 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "10px 12px",
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: ".5px",
    background: "#f8fafc",
    borderBottom: "2px solid #e2e8f0",
    whiteSpace: "nowrap",
    textAlign: "left",
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    color: "#1e293b",
    fontSize: 13,
    verticalAlign: "middle",
  },
  tr: { transition: "background .15s" },
  userCell: { display: "flex", alignItems: "center", gap: 10, minWidth: 180 },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#06b6d4,#3b82f6)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 800,
    flexShrink: 0,
  },
  userName: { fontSize: 13, fontWeight: 700, color: "#0f172a" },
  userSub: { fontSize: 11, color: "#64748b", display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" },
  linkBtn: {
    padding: "6px 12px",
    background: "#eef2ff",
    color: "#4338ca",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 8,
    textDecoration: "none",
    whiteSpace: "nowrap",
  },

  // Pagination
  pagWrap: {
    display: "flex",
    justifyContent: "center",
    gap: 4,
    flexWrap: "wrap",
    padding: "16px 0 4px",
  },
  pagBtn: (disabled) => ({
    padding: "6px 12px",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    color: disabled ? "#cbd5e1" : "#475569",
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    minWidth: 36,
  }),
  pagBtnActive: {
    background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    color: "#fff",
    border: "none",
  },
  pagDots: { padding: "6px 4px", color: "#cbd5e1" },

  // States
  loadingBox: { textAlign: "center", padding: "60px 20px" },
  errorBox: { textAlign: "center", padding: "40px 20px", background: "#fef2f2", borderRadius: 12, border: "1px solid #fecaca" },
  emptyBox: { textAlign: "center", padding: "60px 20px" },
  spinner: {
    width: 48,
    height: 48,
    border: "5px solid #e2e8f0",
    borderTopColor: "#6366f1",
    borderRadius: "50%",
    animation: "convspin 0.8s linear infinite",
    margin: "0 auto",
  },
};

function ResponsiveCSS() {
  return (
    <style>{`
      @keyframes convspin { to { transform: rotate(360deg); } }
      @media (max-width: 640px) {
        h1 { font-size: 20px !important; }
      }
    `}</style>
  );
}
